import { styled } from '@mui/material/styles';
import { Box, Paper, Typography } from '@mui/material';

export const SkillsSection = styled(Box)(({ theme }) => ({
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    paddingTop: theme.spacing(6),
    paddingBottom: theme.spacing(6),
    background: theme.palette.mode === 'dark'
        ? 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.4) 100%)'
        : 'linear-gradient(180deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.03) 100%)',
    position: 'relative',
    '&:before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: theme.palette.mode === 'dark'
            ? 'radial-gradient(circle at 50% 50%, rgba(255,255,255,0.05) 0%, rgba(0,0,0,0) 70%)'
            : 'radial-gradient(circle at 50% 50%, rgba(255,255,255,0.8) 0%, rgba(255,255,255,0) 70%)',
        pointerEvents: 'none',
    }
}));

export const SectionTitle = styled(Typography)(({ theme }) => ({
    fontWeight: 700,
    marginBottom: theme.spacing(8),
    color: theme.palette.primary.main,
    textAlign: 'center',
    position: 'relative',
    '&:after': {
        content: '""',
        position: 'absolute',
        bottom: '-20px',
        left: '50%',
        transform: 'translateX(-50%)',
        width: '60px',
        height: '4px',
        background: theme.palette.primary.main,
        borderRadius: '2px',
    }
}));

export const CategoryTitle = styled(Typography)(({ theme }) => ({
    marginBottom: theme.spacing(3),
    fontWeight: 600,
    position: 'relative',
    display: 'inline-block',
    '&:after': {
        content: '""',
        position: 'absolute',
        bottom: '-8px',
        left: '50%',
        transform: 'translateX(-50%)',
        width: '40px',
        height: '2px',
        borderRadius: '1px',
    }
}));

export const SkillCategory = styled(Paper)(({ theme }) => ({
    padding: theme.spacing(3),
    textAlign: 'center',
    background: theme.palette.mode === 'dark' 
        ? 'linear-gradient(145deg, rgba(255, 255, 255, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)' 
        : 'rgba(255, 255, 255, 0.9)',
    backdropFilter: 'blur(10px)',
    borderRadius: '20px',
    transition: 'all 0.3s ease-in-out',
    border: `1px solid ${theme.palette.mode === 'dark' 
        ? 'rgba(255, 255, 255, 0.08)' 
        : 'rgba(0, 0, 0, 0.1)'}`,
    boxShadow: theme.palette.mode === 'dark'
        ? '0 4px 30px rgba(0, 0, 0, 0.3), inset 0 1px 1px rgba(255, 255, 255, 0.07)'
        : '0 4px 20px rgba(0, 0, 0, 0.08)',
    overflow: 'hidden',
    position: 'relative',
    height: '320px',
    display: 'flex',
    flexDirection: 'column',
    '&:before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        height: '4px',
        background: theme.palette.mode === 'dark'
            ? 'linear-gradient(90deg, rgba(0,0,0,0) 0%, rgba(255,255,255,0.1) 50%, rgba(0,0,0,0) 100%)'
            : 'linear-gradient(90deg, rgba(0,0,0,0) 0%, rgba(0,0,0,0.1) 50%, rgba(0,0,0,0) 100%)',
        transition: 'transform 0.3s ease-in-out',
        transform: 'translateX(-100%)',
    },
    '&:hover': {
        transform: 'translateY(-5px)',
        background: theme.palette.mode === 'dark'
            ? 'linear-gradient(145deg, rgba(255, 255, 255, 0.07) 0%, rgba(255, 255, 255, 0.03) 100%)'
            : 'rgba(255, 255, 255, 1)',
        boxShadow: theme.palette.mode === 'dark'
            ? '0 4px 30px rgba(0, 0, 0, 0.4), inset 0 1px 1px rgba(255, 255, 255, 0.1)'
            : '0 4px 30px rgba(0, 0, 0, 0.12)',
        '&:before': {
            transform: 'translateX(100%)',
        }
    },
}));

export const SkillTag = styled(Box)(({ theme }) => ({
    display: 'inline-block',
    margin: '0.5rem',
    padding: '8px 16px',
    borderRadius: '20px',
    background: theme.palette.mode === 'dark'
        ? 'linear-gradient(145deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.03) 100%)'
        : 'rgba(0, 0, 0, 0.05)',
    backdropFilter: 'blur(5px)',
    color: theme.palette.text.secondary,
    fontSize: '0.9rem',
    fontWeight: 500,
    transition: 'all 0.2s ease-in-out',
    border: `1px solid ${theme.palette.mode === 'dark'
        ? 'rgba(255, 255, 255, 0.12)'
        : 'rgba(0, 0, 0, 0.1)'}`,
    cursor: 'default',
    '&:hover': {
        transform: 'translateY(-2px)',
        background: theme.palette.mode === 'dark'
            ? 'linear-gradient(145deg, rgba(255, 255, 255, 0.1) 0%, rgba(255, 255, 255, 0.05) 100%)'
            : 'rgba(0, 0, 0, 0.1)',
        boxShadow: theme.palette.mode === 'dark'
            ? '0 5px 15px rgba(0,0,0,0.3)'
            : '0 5px 15px rgba(0,0,0,0.1)',
        borderColor: theme.palette.mode === 'dark'
            ? 'rgba(255, 255, 255, 0.2)'
            : 'rgba(0, 0, 0, 0.15)',
    },
}));

export const SkillsContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    overflowY: 'auto',
    flex: 1,
    padding: theme.spacing(1),
    '&::-webkit-scrollbar': {
        width: '6px',
    },
    '&::-webkit-scrollbar-track': {
        background: 'rgba(255, 255, 255, 0.05)',
        borderRadius: '3px',
    },
    '&::-webkit-scrollbar-thumb': {
        background: 'rgba(255, 255, 255, 0.1)',
        borderRadius: '3px',
        '&:hover': {
            background: 'rgba(255, 255, 255, 0.2)',
        },
    },
    scrollbarWidth: 'thin',
    scrollbarColor: 'rgba(255, 255, 255, 0.1) rgba(255, 255, 255, 0.05)',
    paddingRight: theme.spacing(1),
    maskImage: 'linear-gradient(to bottom, black 85%, transparent 100%)',
}));

// Add keyframe animations
const keyframes = `
    @keyframes fadeInUp {
        from {
            opacity: 0;
            transform: translateY(20px);
        }
        to {
            opacity: 1;
            transform: translateY(0);
        }
    }
    @keyframes fadeIn {
        from {
            opacity: 0;
        }
        to {
            opacity: 1;
        }
    }
`;

// Add keyframes to document
const style = document.createElement('style');
style.innerHTML = keyframes;
document.head.appendChild(style);